import { Divider, Stack, Typography, useTheme } from "@carrotfertility/carotene-core"
import React, { useState } from "react"
import { AppointmentsLoading } from "./AppointmentsLoading"
import { BookAppointmentCard } from "./BookAppointmentCard"
import { CancelAppointmentDialog } from "./CancelAppointmentDialog"
import { CarrotCompanionDetails } from "./CarrotCompanionDetails"
import { NotEligibleNote } from "./NotEligibleNote"
import { PastAppointments } from "./PastAppointments"
import { UpNextCard } from "./UpNextCard"
import { useAppointments } from "./useAppointments"
import type { AppointmentsCompanion, AppointmentsPageData, UpcomingAppointment } from "./types"

/**
 * The sections the main column is assembled from, named so each state can be written as one ordered
 * list instead of a JSX branch of its own.
 *
 * The right rail is deliberately not in here: "Other ways to contact Carrot" is present in all ten
 * frames, in every state, and it is the host `PageLayout`'s `sidebar` slot — `AppointmentsSidebar`
 * rather than part of this column.
 */
type MainColumnSection = "upNext" | "past" | "bookAppointment" | "notEligibleNote" | "divider" | "carrotCompanion"

/**
 * Whether the record in the `upcoming` slot is something to head "Up next".
 *
 * `status` is part of the test, not just presence. `AppointmentStatus` admits `"canceled"` and
 * `"completed"`, and nothing stops such a record reaching the `upcoming` slot once the real API fills
 * it (see CONAPP-263). An "Up next" heading over a visit that is already gone claims something untrue
 * — and treating it as nothing upcoming also puts the booking card at the top of the column, which is
 * what that member needs next. The design draws no frame for it, so it is a composition call; the
 * card's own `status` gate (CONAPP-266) stays a backstop rather than the mechanism.
 *
 * A type predicate, so the narrowing it performs is available to callers rather than re-asserted at
 * the point of use — see `upcoming` in the component below.
 */
const hasUpcomingAppointment = (upcoming: UpcomingAppointment | null | undefined): upcoming is UpcomingAppointment =>
  upcoming?.status === "scheduled"

/**
 * The one place the main column's order is decided. Every frame's composition is a list here, so
 * adding a state means adding a list rather than another nested conditional in the JSX below.
 *
 * `"past"` is listed unconditionally: `PastAppointments` returns `null` for an empty or missing list,
 * so the whole section — heading, chevron and grid — disappears together, which is its own business
 * rather than the page's (frame 2 draws no heading over an empty grid). A section that renders `null`
 * contributes no child, so the `Stack`'s sibling spacing does not fire for it and there is no gap to
 * compensate for.
 *
 * `data` is nullable because this tolerates page data with nothing in it — see the test of the same
 * name. That was implicit while it lived in the host, where `strictNullChecks` is off.
 */
export function getMainColumnSections(data: AppointmentsPageData | null | undefined): MainColumnSection[] {
  // Frame 10. The note *replaces* the booking card — `eligible && <BookAppointmentCard />` would
  // leave a hole where the member's explanation belongs — and Up next, Past and the divider go with
  // it. The missing divider is the design's own: `.hr-sec` is drawn in frames 1-8 and absent from
  // frame 10, so there is nothing to separate the note from the Companion heading.
  //
  // Suppressing Past here is a provisional default pending design (CONAPP-270): frame 10 shows none,
  // and it is unclear whether a non-eligible member simply never has history or whether it is
  // deliberately hidden. Implemented as drawn.
  if (!data?.eligible) {
    return ["notEligibleNote", "carrotCompanion"]
  }

  // The flip that the whole ticket is about. Frames 2-5 put the booking card *after* Past, below the
  // visit already booked; frames 1 and 8 have nothing booked, so it leads the column instead.
  return hasUpcomingAppointment(data.upcoming)
    ? ["upNext", "past", "bookAppointment", "divider", "carrotCompanion"]
    : ["bookAppointment", "past", "divider", "carrotCompanion"]
}

export type AppointmentsPageProps = {
  /**
   * The booking route. Injected because the package cannot reach the host's `Paths` enum; the cards
   * build `${bookingPath}/${id}/forms` from it.
   */
  bookingPath: string
  /** The member's Carrot Companion, resolved by the host. Null while it loads or if the read fails. */
  companion: AppointmentsCompanion | null
}

/**
 * The Appointments page's main column.
 *
 * Chrome is the host's: `PageLayout`, `PageHeader` and the sidebar slot live in
 * `src/pages/telemedicine/appointments/page.tsx`, the same split as `TelemedicineBookingPage` and
 * `BookingFlow`.
 */
export function AppointmentsPage({ bookingPath, companion }: AppointmentsPageProps): JSX.Element {
  const theme = useTheme()
  const { data, isLoading, isError } = useAppointments()
  // The page's snapshot of what is being cancelled, held here rather than in the card: confirming
  // removes the appointment, so `data.upcoming` goes null and the card unmounts, while frame 7's
  // canceled dialog has to stay up over a page that has already dropped its Up next section.
  const [appointmentToCancel, setAppointmentToCancel] = useState<UpcomingAppointment | null>(null)

  // Error before loading: a failed read also leaves `data` undefined, so the loading branch would mask it.
  if (isError) {
    return (
      <Typography variant="body1">
        We couldn&rsquo;t load your appointments. Please refresh the page to try again.
      </Typography>
    )
  }
  if (isLoading || !data) {
    return <AppointmentsLoading />
  }

  /*
    Narrowed here, once, rather than asserted at the call site. `getMainColumnSections` only lists
    `"upNext"` for a scheduled appointment, but that reasoning lives in another function and the
    compiler cannot follow it across the boundary — under the host's `strictNullChecks: false` the
    mismatch simply went unremarked, and a comment stood in for the guarantee. Running the same
    predicate the section list runs makes the two agree by construction.
  */
  const upcoming = hasUpcomingAppointment(data?.upcoming) ? data.upcoming : null

  /**
   * Sections are rendered through an explicit `switch`, not a lookup keyed on the section name.
   * `data` derives from the `?apptState=` query param, and CodeQL's
   * `js/unvalidated-dynamic-method-call` fires on *invoking* a function value selected by external
   * input — a `Map` or an object of render functions would both trip it. Same shape as
   * `getScenarioData` in `mock/scenarios.ts`, with the `never` assignment making the compiler reject
   * a section added to the union without a case here.
   */
  const renderMainColumnSection = (section: MainColumnSection): JSX.Element | null => {
    switch (section) {
      case "upNext":
        return <UpNextCard appointment={upcoming} onRequestCancel={setAppointmentToCancel} bookingPath={bookingPath} />
      case "past":
        return <PastAppointments appointments={data?.past} />
      case "bookAppointment":
        return <BookAppointmentCard specialists={data?.specialists} bookingPath={bookingPath} />
      case "notEligibleNote":
        return <NotEligibleNote />
      case "divider":
        return <Divider />
      case "carrotCompanion":
        return <CarrotCompanionDetails companion={companion} />
      default: {
        const unhandled: never = section
        throw new Error(`Unhandled appointments main column section: ${String(unhandled)}`)
      }
    }
  }

  return (
    <>
      <Stack sx={{ flexDirection: "column" }} spacing={theme.spacing(theme.tokens.spacing.xl)}>
        {/*
          Fragments rather than keys inside the `switch`, so the ordering above stays the only place a
          section is named twice. A Fragment is not a DOM node, so the section roots remain the
          `Stack`'s direct children and its sibling spacing still applies.
        */}
        {getMainColumnSections(data).map((section) => (
          <React.Fragment key={section}>{renderMainColumnSection(section)}</React.Fragment>
        ))}
      </Stack>
      {/*
        A sibling of the section list, never a child of it: confirming a cancelation sets
        `data.upcoming` to null, which drops the Up next section, and frame 7 draws the canceled
        dialog over exactly that page. A dialog inside the card, or inside the list above, would
        unmount at the moment it is meant to appear. Mounted only while the flow is live, so each run
        starts on the confirm step.
      */}
      {appointmentToCancel && (
        <CancelAppointmentDialog appointment={appointmentToCancel} onClose={() => setAppointmentToCancel(null)} />
      )}
    </>
  )
}
